import React, { useState, useEffect, useCallback } from 'react'
import s from './VariationWrapperPopup.module.css'
import { Product, ProductPriceResponse } from '../../../types/product'
import { Link } from 'react-router-dom'
import { useCart } from '../../../hooks/useCart'
import {
  buildProductCartMeta,
  getDiscountPercent,
  getEffectivePrice,
  getProductDisplayWeight,
  getSortedVariants,
  getVariantLabel,
  hasCatalogDiscount,
} from '../../../utils/cartUtils'
import { cartIcon } from '../ProductItem/ProductItem'
import { DiscountBadge } from '../ProductMarkers/ProductMarkers'
import { motion } from 'framer-motion'

export default function VariationWrapperPopup({
  product,
  isOpen,
  onClose,
  initialQuantity = 1,
}: {
  product: Product
  isOpen: boolean
  onClose: () => void
  initialQuantity?: number
}) {
  const [quantity, setQuantity] = useState(initialQuantity)
  const [isInCart, setIsInCart] = useState(false)
  const { addItem } = useCart()

  const sortedVariants = getSortedVariants(product.prices || [])
  const [selectedVariant, setSelectedVariant] = useState<ProductPriceResponse | null>(
    sortedVariants[0] ?? null,
  )

  const hasVariants = sortedVariants.length > 0
  const effectivePrice = selectedVariant ? getEffectivePrice(selectedVariant) : null

  useEffect(() => {
    setQuantity(initialQuantity)
  }, [initialQuantity])

  // Re-sync selected variant when product changes (e.g. different product opened)
  useEffect(() => {
    const sorted = getSortedVariants(product.prices || [])
    setSelectedVariant(sorted[0] ?? null)
  }, [product.id, product.prices])

  const decreaseQuantity = () => {
    setQuantity(q => Math.max(1, q - 1))
  }

  const increaseQuantity = () => {
    setQuantity(quantity + 1)
  }

  const handleClose = useCallback(() => {
    setIsInCart(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return

    window.scrollTo({ top: 0, behavior: 'auto' })

    const originalOverflow = document.body.style.overflow
    const originalPosition = document.body.style.position
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'

    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.position = originalPosition
      document.body.style.width = ''
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, handleClose])

  const getWeight = () => getProductDisplayWeight(product, selectedVariant)

  const handleAddToCart = () => {
    if (!selectedVariant) return
    const cartItem = {
      id: parseInt(product.id),
      name: product.name,
      price: effectivePrice ?? 0,
      regular_price: selectedVariant.price,
      sale_price: hasCatalogDiscount(selectedVariant.discount)
        ? (effectivePrice ?? undefined)
        : undefined,
      discount_percent: getDiscountPercent(selectedVariant.discount),
      image: product.mainPicture || '/images/product.png',
      sku: product.id,
      quantity: quantity,
      chosenVariantId: selectedVariant.id,
      variants: sortedVariants,
      meta_data: buildProductCartMeta(product, { variant: selectedVariant }),
    }

    addItem(cartItem)
    setIsInCart(true)
    onClose()
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`${s.layout} ${isOpen ? s.open : ''}`}
      onClick={handleClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: isOpen ? 1 : 0, y: isOpen ? 0 : 100 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={s.popup}
        onClick={e => e.stopPropagation()}
      >
        <div className={s.variationWrapper}>
          <div className={s.topInfo}>
            <div className={s.header}>
              <h2 className={s.productName}>{product.name}</h2>
              <button onClick={handleClose} aria-label="Закрити">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                >
                  <rect
                    x="1"
                    y="1"
                    width="20"
                    height="20"
                    rx="6"
                    stroke="#BFBFBF"
                    strokeWidth="1.25"
                  />
                  <path
                    d="M8.87839 13.1215L13.121 8.87891"
                    stroke="#BFBFBF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.87839 8.87894L13.121 13.1216"
                    stroke="#BFBFBF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Weight / variant pills */}
            {sortedVariants.length > 1 && (
              <div className={s.variantPills}>
                {sortedVariants.map(variant => (
                  <button
                    key={variant.id}
                    className={`${s.variantPill} ${selectedVariant?.id === variant.id ? s.variantPillActive : ''}`}
                    onClick={() => setSelectedVariant(variant)}
                  >
                    {getVariantLabel(product, variant)}
                  </button>
                ))}
              </div>
            )}
            {sortedVariants.length === 1 && selectedVariant && (
              <div className={s.variation}>
                <span className={s.variationTitle}>Фасування</span>
                <span className={s.variationValue}>{getVariantLabel(product, selectedVariant)}</span>
              </div>
            )}

            {product.variants && product.variants.length > 0 && (
              <>
                {product.variants.map((variant, index) => (
                  <div className={s.variation} key={index}>
                    <span className={s.variationTitle}>{variant.label}</span>
                    <span className={s.variationValue}>{variant.value}</span>
                  </div>
                ))}
              </>
            )}

            <div className={s.variation}>
              <span className={s.variationTitle}>Кількість</span>
              <div className={s.quantity}>
                <button
                  className={`${s.quantityButton} ${quantity <= 1 ? s.disabled : ''}`}
                  onClick={decreaseQuantity}
                  disabled={quantity <= 1}
                >
                  <svg
                    width="10"
                    height="2"
                    viewBox="0 0 10 2"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.25 1L8.75 1"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <span className={s.quantityValue}>{quantity}</span>
                <button className={s.quantityButton} onClick={increaseQuantity}>
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.25 15H18.75"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15 11.2499L15 18.7499"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className={s.infoWrapper}>
            <div className={s.priceWrapper}>
              {selectedVariant && hasCatalogDiscount(selectedVariant.discount) ? (
                <div className={s.priceDiscount}>
                  <p className={s.price}>{effectivePrice} ₴</p>
                  <p className={s.originalPrice}>{selectedVariant.price} ₴</p>
                  <DiscountBadge discount={selectedVariant.discount} />
                </div>
              ) : (
                <p className={s.price}>
                  {hasVariants ? `${effectivePrice} ₴` : 'Ціна не вказана'}
                </p>
              )}
              {getWeight() && (
                <p className={s.packagingWeight}>
                  / {getWeight()}
                </p>
              )}
            </div>
            <div className={s.buttonsWrapper}>
              <button
                className={`${s.button} ${isInCart ? s.done : ''}`}
                onClick={handleAddToCart}
                disabled={!hasVariants}
              >
                {cartIcon()}
                <span>В кошик</span>
              </button>
              <Link to={`/product/${product.slug}`} className={s.button}>
                <span>Детальніше</span>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}
